import React, { useState, useEffect } from 'react';
import { XCircle, ArrowLeft, Leaf, CreditCard, Loader2 } from 'lucide-react';
import apiClient from '../utils/apiClient';

const PaymentCancelled = () => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState(null);
  const [storedEmail, setStoredEmail] = useState(null);

  // Check for stored email on component mount
  useEffect(() => {
    const storedData = localStorage.getItem('pending_registration_email');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        setStoredEmail(parsedData.email);
      } catch (error) {
        console.error('Error parsing stored email:', error);
        localStorage.removeItem('pending_registration_email');
      }
    }
  }, []);

  const handleTryAgain = async () => {
    if (!storedEmail) {
      // No stored email, redirect to registration form
      window.location.href = '/?register=premium';
      return;
    }

    setIsRetrying(true);
    setRetryError(null);

    try {
      const result = await apiClient.post('/auth/retry-payment', { email: storedEmail });
      
      if (result.success && result.checkout_url) {
        // Clear stored data and redirect to checkout
        localStorage.removeItem('pending_registration_email');
        window.location.href = result.checkout_url;
      } else {
        setRetryError('Failed to create new checkout session. Please try registering again.');
      }
    } catch (error) {
      console.error('Payment retry error:', error);
      setRetryError(error.message || 'Failed to retry payment. Please try again.');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleContinueFree = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-red-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {/* Cancelled Icon */}
          <div className="mx-auto flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-center mb-4">
            <Leaf className="text-green-600 mr-2" size={24} />
            <span className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
              WeederBoard
            </span>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Payment Cancelled
          </h1>
          
          <p className="text-gray-600 mb-6">
            Your payment was cancelled and no charges were made to your account. You can try again or continue with the free plan.
          </p>

          {/* Error Message */}
          {retryError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-600 text-sm">{retryError}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleTryAgain}
              disabled={isRetrying}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-4 rounded-lg font-bold text-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isRetrying ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                  Creating New Checkout Session...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 w-5 h-5" />
                  Try Payment Again
                </>
              )}
            </button>

            <button
              onClick={handleContinueFree}
              className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center"
            >
              <ArrowLeft className="mr-2 w-5 h-5" />
              Back to the public Leaderboard
            </button>
          </div>

          {/* Free Plan Features */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-6">
            <h3 className="font-semibold text-gray-800 mb-2">Free Plan Includes:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Daily Deals Leaderboard</li>
              <li>• Search by State filter</li>
              <li>• Flower and Concentrate deals only</li>
            </ul>
          </div>

          <p className="text-sm text-gray-500 mt-4">
            You can upgrade to premium at any time from your account settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentCancelled;
